import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import aguid from 'lib/aguid';

import EntityInformationTableForm from './EntityInformationTableForm';

const buildEmptyFieldRow =
  (): VisualisationDefinitions.EntityInformationTableFieldRow => ({
    key: aguid(),
    type: 'field',
    datasetId: '',
    field: '',
  });

const buildEmptyMetricRow =
  (): VisualisationDefinitions.EntityInformationTableMetricRow => ({
    key: aguid(),
    type: 'metric',
    metricId: '',
  });

const EntityInformationTableFormContainer = ({
  gadget,
  onChange,
  isReport = false,
}: {
  gadget?: VisualisationDefinitions.EntityInformationTable;
  onChange?: React.Dispatch<
    React.SetStateAction<VisualisationDefinition | undefined>
  >;
  isReport?: boolean;
}) => {
  const { datasets } = useContext(DatasetDefinitionsContext);

  const [id] = useState<string>(gadget ? gadget.id : aguid());
  const [name, setName] = useState<string>(gadget ? gadget.name : '');
  const [description, setDescription] = useState<string>(
    gadget ? gadget.description : '',
  );
  const [rows, setRows] = useState<
    VisualisationDefinitions.EntityInformationTableRow[]
  >(gadget ? gadget.rows : []);
  const [datasetSorts, setDatasetSorts] = useState<
    Record<string, VisualisationDefinitions.EntityInformationTableDatasetSort>
  >(gadget?.datasetSorts ?? {});
  const [columnsPerRow, setColumnsPerRow] = useState<1 | 2>(
    gadget?.columnsPerRow ?? 1,
  );
  const [renderMode, setRenderMode] = useState<'grid' | 'cards'>(
    gadget?.renderMode ?? 'cards',
  );

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onDescriptionChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const datasetOptions = useMemo<DropdownOption[]>(
    () =>
      datasets.map((d) => ({
        label: d.name || d.type,
        key: d.type,
        onSelected: () => {},
      })),
    [datasets],
  );

  const fieldsByDatasetId = useMemo<
    Record<string, Array<{ field: string; label: string }>>
  >(() => {
    const result: Record<string, Array<{ field: string; label: string }>> = {};
    datasets.forEach((d) => {
      result[d.type] = d.fields.map((f) => ({
        field: f.field,
        label: f.alias || f.field,
      }));
    });
    return result;
  }, [datasets]);

  const onRowDatasetChanged = useCallback((key: string, datasetId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.key === key && r.type === 'field'
          ? { ...r, datasetId, field: '' }
          : r,
      ),
    );
  }, []);

  const onRowFieldChanged = useCallback((key: string, field: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.key === key && r.type === 'field' ? { ...r, field } : r,
      ),
    );
  }, []);

  const onRowMetricChanged = useCallback(
    (key: string, metricId: string | undefined) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key && r.type === 'metric'
            ? { ...r, metricId: metricId ?? '' }
            : r,
        ),
      );
    },
    [],
  );

  const onRemoveRow = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const onRowReportDrillDownChanged = useCallback(
    (key: string, id: string | undefined) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key
            ? id !== undefined
              ? { ...r, reportDrillDownId: id, boardDrillDownId: undefined }
              : { ...r, reportDrillDownId: undefined }
            : r,
        ),
      );
    },
    [],
  );

  const onRowBoardDrillDownChanged = useCallback(
    (key: string, id: string | undefined) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key
            ? id !== undefined
              ? { ...r, boardDrillDownId: id, reportDrillDownId: undefined }
              : { ...r, boardDrillDownId: undefined }
            : r,
        ),
      );
    },
    [],
  );

  const onAddFieldRow = useCallback(() => {
    setRows((prev) => [...prev, buildEmptyFieldRow()]);
  }, []);

  const onAddMetricRow = useCallback(() => {
    setRows((prev) => [...prev, buildEmptyMetricRow()]);
  }, []);

  const onRowTrendingChanged = useCallback(
    (
      key: string,
      trending:
        | VisualisationDefinitions.EntityInformationTableMetricRowTrending
        | undefined,
    ) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key && r.type === 'metric' ? { ...r, trending } : r,
        ),
      );
    },
    [],
  );

  const onRowsReordered = useCallback(
    (newOrder: VisualisationDefinitions.EntityInformationTableRow[]) => {
      setRows(newOrder);
    },
    [],
  );

  const onDatasetSortFieldChanged = useCallback(
    (datasetId: string, sortByField: string) => {
      setDatasetSorts((prev) => ({
        ...prev,
        [datasetId]: { ...prev[datasetId], sortByField },
      }));
    },
    [],
  );

  const onDatasetSortDirectionChanged = useCallback(
    (datasetId: string, sortByDirection: SortBy) => {
      setDatasetSorts((prev) => ({
        ...prev,
        [datasetId]: { ...prev[datasetId], sortByDirection },
      }));
    },
    [],
  );

  const draftGadget = useMemo(():
    | VisualisationDefinitions.EntityInformationTable
    | undefined => {
    const allRowsValid = rows.every((r) => {
      if (r.type === 'field') return r.datasetId !== '' && r.field !== '';
      return r.metricId !== '';
    });
    if (name !== '' && rows.length > 0 && allRowsValid) {
      return {
        id,
        type: 'EntityInformationTable',
        name,
        description,
        rows,
        datasetSorts,
        columnsPerRow,
        renderMode,
      };
    }
    return undefined;
  }, [id, name, description, rows, datasetSorts, columnsPerRow, renderMode]);

  useEffect(() => {
    if (draftGadget && onChange) {
      onChange(draftGadget);
    }
  }, [draftGadget, onChange]);

  return (
    <EntityInformationTableForm
      name={name}
      onNameChanged={onNameChanged}
      description={description}
      onDescriptionChanged={onDescriptionChanged}
      datasetOptions={datasetOptions}
      fieldsByDatasetId={fieldsByDatasetId}
      rows={rows}
      datasetSorts={datasetSorts}
      onRowDatasetChanged={onRowDatasetChanged}
      onRowFieldChanged={onRowFieldChanged}
      onRowMetricChanged={onRowMetricChanged}
      onRemoveRow={onRemoveRow}
      onRowReportDrillDownChanged={onRowReportDrillDownChanged}
      onRowBoardDrillDownChanged={onRowBoardDrillDownChanged}
      onDatasetSortFieldChanged={onDatasetSortFieldChanged}
      onDatasetSortDirectionChanged={onDatasetSortDirectionChanged}
      onAddFieldRow={onAddFieldRow}
      onAddMetricRow={onAddMetricRow}
      onRowTrendingChanged={onRowTrendingChanged}
      onRowsReordered={onRowsReordered}
      columnsPerRow={columnsPerRow}
      onColumnsPerRowChanged={setColumnsPerRow}
      renderMode={renderMode}
      onRenderModeChanged={setRenderMode}
      isReport={isReport}
    />
  );
};

export default EntityInformationTableFormContainer;
